import { useEffect, useState, type ReactNode } from 'react';
import { Capacitor } from '@capacitor/core';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import {
  biometryLabel,
  isLockEnabled,
  promptBiometric,
} from '@/lib/biometric-lock';
import { Fingerprint, Lock } from 'lucide-react';

/**
 * Wraps the authenticated app and, on cold start, presents a full-screen
 * biometric unlock prompt before rendering children. Only renders the
 * lock UI when:
 *   - native platform (web bypass),
 *   - user is signed in via email (not PIN-session — shared kiosks),
 *   - the per-profile lock preference is enabled.
 *
 * Lifecycle: locked once per cold start. Background → foreground resume
 * does NOT re-lock (deliberate; see lib/biometric-lock.ts header).
 */
export function BiometricLockGuard({ children }: { children: ReactNode }) {
  const { profile, loginMethod, signOut } = useAuth();
  const [checked, setChecked] = useState(false);
  const [shouldLock, setShouldLock] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [label, setLabel] = useState('Biometric');
  const [attempting, setAttempting] = useState(false);

  const isNative = Capacitor.isNativePlatform();

  // Decide whether to lock — one-shot per session.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isNative || loginMethod !== 'email' || !profile?.id) {
        if (!cancelled) {
          setShouldLock(false);
          setChecked(true);
        }
        return;
      }
      const enabled = await isLockEnabled(profile.id);
      if (cancelled) return;
      setShouldLock(enabled);
      setChecked(true);
      if (enabled) {
        const l = await biometryLabel();
        if (!cancelled) setLabel(l);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isNative, loginMethod, profile?.id]);

  // Auto-prompt on first render of the lock screen.
  useEffect(() => {
    if (!checked || !shouldLock || unlocked || attempting) return;
    void attemptUnlock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checked, shouldLock, unlocked]);

  const attemptUnlock = async () => {
    setAttempting(true);
    const ok = await promptBiometric(`Unlock DashAlign with ${label}`);
    setAttempting(false);
    if (ok) setUnlocked(true);
  };

  const handleSignOut = async () => {
    await signOut();
  };

  // Render nothing (still rendering children below) until we know whether
  // to lock — avoids a one-frame flash of dashboard content.
  if (!checked) return null;
  if (!shouldLock || unlocked) return <>{children}</>;

  return (
    <div className="fixed inset-0 z-[200] bg-background flex flex-col items-center justify-center px-6">
      <div className="flex flex-col items-center gap-6 max-w-sm w-full">
        <div className="rounded-full bg-primary/10 p-6">
          <Lock className="h-12 w-12 text-primary" />
        </div>
        <div className="text-center space-y-2">
          <h1 className="text-2xl font-bold">DashAlign is locked</h1>
          <p className="text-muted-foreground text-sm">
            Use {label} to unlock and continue.
          </p>
        </div>
        <Button
          size="lg"
          className="w-full"
          onClick={attemptUnlock}
          disabled={attempting}
        >
          <Fingerprint className="mr-2 h-5 w-5" />
          {attempting ? 'Waiting…' : `Unlock with ${label}`}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleSignOut}
          className="text-muted-foreground"
        >
          Sign out instead
        </Button>
      </div>
    </div>
  );
}
